/*
 * @Copyright 2024-2025. Institute for Future Intelligence, Inc.
 */

import styled from 'styled-components';
import { App, Avatar, Button, Divider, Dropdown, MenuProps, Space } from 'antd';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useStore } from './stores/common';
import * as Selector from './stores/selector';
import { usePrimitiveStore } from './stores/commonPrimitive';
import { AntdMenuItem } from './components/menuItem';
import { useTranslation } from 'react-i18next';
import { AppstoreOutlined, BulbOutlined, ReadOutlined, WarningOutlined } from '@ant-design/icons';
import { useIsMobile } from './hooks';
import { askToSaveThen } from './saveReminder.tsx';
import LegalAcceptancePrompt from './legal/legalAcceptancePrompt';
import { LEGAL_VERSION } from './legal/legalVersion';
import { readCachedAcceptance, writeCachedAcceptance } from './legal/legalAcceptanceCache';
import ScreenNameModal from './components/screenNameModal';

const ButtonsContainer = styled.div<{ $mobile?: boolean }>`
  position: absolute;
  top: 0;
  right: 10px;
  margin: 0;
  padding-bottom: 0;
  // Desktop header is 72px with padding-top:10px; on mobile the title row is
  // 48px tall, so push the avatar/Sign In down 8px to vertically center it
  // alongside the hamburger and the AIMS title.
  padding-top: ${(p) => (p.$mobile ? '8px' : '24px')};
  display: flex;
  justify-content: center;
  align-items: center;
  user-select: none;
  z-index: 9;
`;

export interface MainToolBarProps {
  signIn: () => void;
  signInAnonymously: () => void;
  signOut: () => void;
}

const MainToolBar = React.memo(({ signIn, signInAnonymously, signOut }: MainToolBarProps) => {
  const language = useStore(Selector.language);
  const user = useStore(Selector.user);
  const isMobile = useIsMobile();

  const { modal } = App.useApp();
  const { t } = useTranslation();
  const lang = useMemo(() => {
    return { lng: language };
  }, [language]);

  // Sign-in must be gated behind an explicit Terms / Privacy acceptance.
  // Clicking a sign-in choice stashes the *action* here; the gate, on
  // accept, writes the acceptance into the primitive store (so
  // registerUser can persist it to Firestore once the auth call returns)
  // and then runs the stashed action. Decline closes the gate without
  // touching auth.
  const [pendingSignIn, setPendingSignIn] = useState<(() => void) | null>(null);

  // The screen-name prompt: raised by useAuthHandlers right after an anonymous
  // sign-in, and by the avatar menu at any time afterwards. One piece of local
  // state drives both so the modal can't be open twice.
  const askForScreenName = usePrimitiveStore(Selector.askForScreenName);
  const [showScreenName, setShowScreenName] = useState(false);
  const closeScreenName = () => {
    setShowScreenName(false);
    usePrimitiveStore.getState().set((state) => {
      state.askForScreenName = false;
    });
  };

  // For Google sign-in: if this browser already has a cached acceptance
  // for the current LEGAL_VERSION, skip the modal. The Firestore record
  // remains authoritative; the cache is just a UX optimization so the
  // same person on the same browser isn't re-prompted every session.
  //
  // For anonymous sign-in: never skip. Each anonymous session creates a
  // brand-new Firebase user, so we want a fresh, explicit acceptance
  // before the new account is provisioned.
  const requestSignIn = (action: () => void, useCache: boolean) => {
    if (useCache && readCachedAcceptance() === LEGAL_VERSION) {
      usePrimitiveStore.getState().set((state) => {
        state.pendingAcceptance = {
          acceptedTermsAt: new Date().toISOString(),
          acceptedTermsVersion: LEGAL_VERSION,
        };
      });
      action();
      return;
    }
    setPendingSignIn(() => action);
  };

  const onAcceptGate = () => {
    const now = new Date().toISOString();
    usePrimitiveStore.getState().set((state) => {
      state.pendingAcceptance = { acceptedTermsAt: now, acceptedTermsVersion: LEGAL_VERSION };
    });
    writeCachedAcceptance(LEGAL_VERSION);
    const action = pendingSignIn;
    setPendingSignIn(null);
    if (action) action();
  };

  const onDeclineGate = () => {
    setPendingSignIn(null);
  };

  const anonymousSignInCheck = () => {
    modal.confirm({
      title: `${t('message.SigningInAnonymousAccount', lang)}`,
      icon: <WarningOutlined />,
      type: 'warning',
      onOk: () => {
        signInAnonymously();
      },
      onCancel: () => {},
      okText: `${t('word.Yes', lang)}`,
      cancelText: `${t('word.No', lang)}`,
    });
  };

  // A chat's sign-in prompt asks for this menu (signInMenuRequest).
  const [signInMenuOpen, setSignInMenuOpen] = useState(false);
  const signInMenuRequest = usePrimitiveStore((s) => s.signInMenuRequest);
  // The counter lives in the store and outlives this toolbar, which unmounts
  // whenever the landing page shows (Help → Welcome). Open only for a request
  // made AFTER this mount: on mount, every earlier request counts as handled,
  // or the menu would pop open by itself on each return from the landing page.
  const handledSignInRequest = useRef(signInMenuRequest);
  useEffect(() => {
    if (signInMenuRequest > handledSignInRequest.current) {
      handledSignInRequest.current = signInMenuRequest;
      setSignInMenuOpen(true);
    }
  }, [signInMenuRequest]);

  const guardedSignIn = () => requestSignIn(() => signIn(), true);
  const guardedAnonymousSignIn = () => requestSignIn(() => anonymousSignInCheck(), false);

  const signOutCheck = () => {
    if (user.anonymous) {
      modal.confirm({
        title: `${t('message.SigningOutAnonymousAccount', lang)}`,
        icon: <WarningOutlined />,
        type: 'warning',
        keyboard: false, // disable Escape key so that onCancel is not called when Escape is pressed
        onOk: () => {
          // swap OK and Cancel so that ENTER maps to cancel
        },
        onCancel: () => {
          signOut();
        },
        okText: `${t('word.No', lang)}`,
        cancelText: `${t('word.Yes', lang)}`,
      });
    } else {
      // Signed-in user: signing out drops their ability to save the current
      // project, so warn about unsaved edits first (No → sign out anyway).
      askToSaveThen(modal, () => signOut());
    }
  };

  const avatarMenu: MenuProps['items'] = [
    {
      key: 'account',
      label: (
        <AntdMenuItem
          onClick={() => {
            // Account Settings is now a standalone page (/aims/account), not a
            // floating panel — open it in a new tab so in-progress chamber
            // work isn't disturbed (same as the Commons / Tutorial shortcuts).
            window.open((import.meta.env.BASE_URL || '/') + 'account', '_blank', 'noopener');
          }}
        >
          {t('avatarMenu.AccountSettings', lang)}
        </AntdMenuItem>
      ),
    },
    // Anonymous users can upgrade in place: signIn() detects isAnonymous and
    // calls linkWithPopup, preserving the UID and all their projects.
    ...(user.anonymous
      ? [
          // The way back to the screen-name prompt after it has been skipped —
          // usually needed the moment the student joins a class and realizes
          // their teacher is looking at a roster row that just says "Anonymous".
          {
            key: 'screen-name',
            label: (
              <AntdMenuItem onClick={() => setShowScreenName(true)}>
                {t('accountSettingsPanel.ScreenName', lang)}
              </AntdMenuItem>
            ),
          },
          {
            key: 'upgrade-to-google',
            label: <AntdMenuItem onClick={signIn}>{t('avatarMenu.UpgradeToGoogle', lang)}</AntdMenuItem>,
          },
        ]
      : []),
    {
      key: 'sign-out',
      label: <AntdMenuItem onClick={signOutCheck}>{t('avatarMenu.SignOut', lang)}</AntdMenuItem>,
    },
  ];

  const signInMenu: MenuProps['items'] = [
    {
      key: 'signin-default',
      label: <AntdMenuItem onClick={guardedSignIn}>{t('avatarMenu.SignInAsMe', lang)}</AntdMenuItem>,
    },
    {
      key: 'signin-anonymously',
      label: <AntdMenuItem onClick={guardedAnonymousSignIn}>{t('avatarMenu.SignInAnonymously', lang)}</AntdMenuItem>,
    },
  ];

  // Open the Commons / Tutorial in a new tab so the user's in-progress
  // chamber work isn't disturbed. Uses the configured base path so the
  // URLs stay correct under Vite's base in prod.
  const base = import.meta.env.BASE_URL || '/';
  const openCommons = () => window.open(base + 'commons', '_blank', 'noopener');
  const openTutorial = () => window.open(base + 'tutorial', '_blank', 'noopener');
  // Toggles the left-edge examples drawer mounted by cloudManager. State
  // lives on the primitive store so the drawer and this button stay in sync
  // and other surfaces can open it too.
  const openExamples = () =>
    usePrimitiveStore.getState().set((state) => {
      state.showExamplesDrawer = true;
    });

  return (
    <ButtonsContainer $mobile={isMobile}>
      <Space direction="horizontal">
        {/* Examples opens a left-edge drawer and is shown on every viewport
            — it's the fastest way for a mobile visitor to land on a working
            scene. Commons / Tutorial stay desktop-only (both remain reachable
            from the main menu's Help submenu) so the mobile title row keeps
            its space for the Save pill. */}
        <Space direction="horizontal" size={8}>
          <Button
            type="text"
            icon={<BulbOutlined />}
            onClick={openExamples}
            title={t('tooltip.openExamples', lang)}
            aria-label={t('tooltip.openExamples', lang)}
            style={{ padding: 0, margin: 0, width: 'auto', minWidth: 0 }}
          />
          {!isMobile && (
            <>
              <Button
                type="text"
                icon={<AppstoreOutlined />}
                onClick={openCommons}
                title={t('tooltip.openCommons', lang)}
                aria-label={t('tooltip.openCommons', lang)}
                style={{ padding: 0, margin: 0, width: 'auto', minWidth: 0 }}
              />
              <Button
                type="text"
                icon={<ReadOutlined />}
                onClick={openTutorial}
                title={t('tooltip.openTutorial', lang)}
                aria-label={t('tooltip.openTutorial', lang)}
                style={{ padding: 0, margin: 0, width: 'auto', minWidth: 0 }}
              />
            </>
          )}
        </Space>
        <Divider type="vertical" style={{ height: '20px', margin: 0 }} />
        <div style={{ verticalAlign: 'top' }}>
          {user.displayName ? (
            <Dropdown menu={{ items: avatarMenu }} trigger={['click']}>
              <a
                className="ant-dropdown-link"
                onClick={(e) => e.preventDefault()}
                title={t('tooltip.clickToAccessAccountSettings', lang)}
              >
                <Avatar size={32} src={user.photoURL} alt={user.displayName} />
              </a>
            </Dropdown>
          ) : (
            <>
              {!isMobile && (
                <span style={{ paddingRight: '10px', fontSize: '12px' }}>
                  {t('message.ToSaveYourWorkPleaseSignIn', lang)}
                </span>
              )}
              <Dropdown
                menu={{ items: signInMenu, onClick: () => setSignInMenuOpen(false) }}
                trigger={['click']}
                open={signInMenuOpen}
                onOpenChange={setSignInMenuOpen}
              >
                <Button type="primary">{t('avatarMenu.SignIn', lang)}</Button>
              </Dropdown>
            </>
          )}
        </div>
      </Space>
      {pendingSignIn && <LegalAcceptancePrompt onAccept={onAcceptGate} onDecline={onDeclineGate} />}
      <ScreenNameModal open={showScreenName || askForScreenName} onClose={closeScreenName} />
    </ButtonsContainer>
  );
});

export default MainToolBar;
